import React from 'react';
import { TextField, TextFieldProps } from '@mui/material';

export function digitsOnly(s: string): string {
  return s.replace(/\D/g, '');
}

/**
 * Kullanıcının girdiği metni TR cep telefonu için 10 haneye normalize eder.
 * Kabul edilen girişler: +905..., +90 5..., 905..., 05..., 5... (rakam dışı karakterler temizlenir).
 * Sonuç: en fazla 10 hane, 5 ile başlar (5xxxxxxxxx) ya da boş string.
 */
export function normalizeTurkishMobileTo10Digits(raw: string): string {
  let digits = digitsOnly(raw);
  if (!digits.length) return '';

  // Sadece ülke kodu girildiyse (örn: "+90") state'i boşalt.
  if (digits === '90') return '';

  if (digits.length > 10) {
    if (digits.startsWith('90') && digits[2] === '5') digits = digits.slice(2, 12);
    else if (digits.startsWith('0') && digits[1] === '5') digits = digits.slice(1, 11);
    else digits = digits.slice(0, 10);
  }

  if (digits.length && digits[0] !== '5') {
    if (digits.startsWith('0') && digits[1] === '5') digits = digits.slice(1);
    else if (digits.startsWith('90') && digits[2] === '5') digits = digits.slice(2);
    else digits = '5' + digits.slice(0, 9);
  }

  return digits.slice(0, 10);
}

/** Gösterim formatı: +90 5XX XXX XX XX */
export function formatTurkishMobileDisplay(digits10: string): string {
  const d = digitsOnly(digits10).slice(0, 10);
  if (!d.length) return '';
  const after5 = d[0] === '5' ? d.slice(1) : d;
  if (after5.length <= 2) return `+90 5${after5}`;
  if (after5.length <= 5) return `+90 5${after5.slice(0, 2)} ${after5.slice(2)}`;
  if (after5.length <= 7) return `+90 5${after5.slice(0, 2)} ${after5.slice(2, 5)} ${after5.slice(5)}`;
  return `+90 5${after5.slice(0, 2)} ${after5.slice(2, 5)} ${after5.slice(5, 7)} ${after5.slice(7, 9)}`;
}

/** 10 haneli (5xxxxxxxxx) TR cep numarası mı */
export function isValidTurkishMobileDigits10(digits10: string): boolean {
  return /^5\d{9}$/.test(digitsOnly(digits10).slice(0, 10));
}

/** 10 haneli 5 ile başlayan TR cep numarası -> API için 905xxxxxxxxx */
export function toApiPhone90(digits10: string): string {
  const d = digitsOnly(digits10).slice(0, 10);
  if (!/^5\d{9}$/.test(d)) return '';
  return '90' + d;
}

export type TurkishMobilePhoneInputProps = Omit<TextFieldProps, 'value' | 'onChange'> & {
  /** Sadece rakam olacak şekilde 10 hane (5xxxxxxxxx) ya da boş */
  valueDigits: string;
  /** Güncellenmiş 10 haneli digit string */
  onChangeDigits: (digits10: string) => void;
  /** Boşken input içinde gösterilecek değer */
  emptyDisplayValue?: string;
};

const TurkishMobilePhoneInput: React.FC<TurkishMobilePhoneInputProps> = ({
  valueDigits,
  onChangeDigits,
  emptyDisplayValue = '+90 5',
  inputProps,
  placeholder = '+90 5',
  ...props
}) => {
  const displayValue = valueDigits ? formatTurkishMobileDisplay(valueDigits) : emptyDisplayValue;

  return (
    <TextField
      {...props}
      placeholder={placeholder}
      value={displayValue}
      onChange={(e) => onChangeDigits(normalizeTurkishMobileTo10Digits(e.target.value))}
      inputProps={{
        inputMode: 'numeric',
        maxLength: 18,
        autoComplete: 'tel',
        ...inputProps,
      }}
    />
  );
};

export default TurkishMobilePhoneInput;
